import { DataGridPro, GridColDef, GridRowParams, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, useGridApiRef } from '@mui/x-data-grid-pro';
import * as AssignmentAtoms from 'Atoms/AssignmentAtoms';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import * as GridAtoms from '../../Atoms/WorkAssignmentGridAtoms';
import * as AssignAtoms from '../../Atoms/AssignmentAtoms';
import { GridPanelContent } from './GridPanelContent';
import RightFilterDrawer from './RightFilterDrawer';
import FilterListIcon from "@mui/icons-material/FilterList";
import { Badge, Button, Paper, Stack, TextField } from '@mui/material';
import Checkbox from "./Checkbox";
import AddIcon from '@mui/icons-material/Add';
import AssignmentDialog from 'components/Assignment/AssignmentDialog';
import { useTranslation } from "react-i18next";
import i18next from 'i18next';
import useWorkCol from './WorkAssignmentColumns';
import { useColumnVisibility } from '../../hooks/ColumnVisibilityHook';
import { useEffect, useState } from 'react';

interface WorkAssignmentGridProps {
  gridHeight: string;
}
const WorkAssignmentGrid = ({ gridHeight }: WorkAssignmentGridProps) => {
  const [t,] = useTranslation();
  const [workAssignmentData] = useAtom(GridAtoms.workAssignmentDataAtom);
  const [workAssignmentInitialData] = useAtom(GridAtoms.initialWADataAtom);
  const [selectedOperationIds, setSelectedOperationIds] = useAtom(AssignmentAtoms.selectedOperationIdsAtom);
  const [showAssignDialog, setShowAssignDialog] = useAtom(
    AssignAtoms.openAssignmentDialogAtom
  );
  const [assignmentDialogType, setAssignmentDialogType] = useState<string>("");
  const gridColumns: GridColDef[] = useWorkCol();
  const { columnVisibility, handleColumnVisibilityChange } = useColumnVisibility();
  const apiRef = useGridApiRef();
  const [refreshGridData] = useAtom(GridAtoms.refreshGridDataAtom);

  useEffect(() => {
    if (apiRef.current) {
      apiRef.current.setFilterModel({ items: [] });
    }
  }, [refreshGridData, apiRef]);

  useEffect(() => {
    if (selectedOperationIds.length === 0) return;
    
    const selectedIds = new Set(selectedOperationIds.map(op => op.id));
    const updatedOperations = workAssignmentData.filter(row => selectedIds.has(row.id));
    
    const hasChanges = updatedOperations.some((updated) => {
      const old = selectedOperationIds.find(op => op.id === updated.id);
      return updated.assignedUsers.length !== old?.assignedUsers.length;
    });
    
    if (hasChanges && updatedOperations.length > 0) {
      setSelectedOperationIds(updatedOperations);
    }
  }, [workAssignmentData, selectedOperationIds]);

  // drawer state
  const [open, setOpen] = useAtom(GridAtoms.drawerOpenAtom);
  const [gridDataLoading,] = useAtom(GridAtoms.gridDataLoadingAtom);
  const customParseFormat = require('dayjs/plugin/customParseFormat');
  dayjs.extend(customParseFormat);

  const handleDrawerOpen = () => {
    setOpen(!open);
  };

  const handleAssignment = (actionType:string) => {
    setAssignmentDialogType(actionType);
    setShowAssignDialog(true);
  };
    
  const [filterCount] = useAtom(GridAtoms.sidebarFilterCountAtom);


  const dataGridFilter = () => {
    const hasAssignedUsers = selectedOperationIds.some(op => op.assignedUsers.length > 0);
    return (
      <GridToolbarContainer>
        <Stack width={'100%'} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mb: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
          {showAssignDialog  && <AssignmentDialog AssignmentType={assignmentDialogType}/>}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            color="primary"
            onClick={()=>handleAssignment("Assign")}
            disabled={selectedOperationIds.length < 1 || showAssignDialog}
            size="small"
          >
            {t("AssignText")}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            color="primary"
            onClick={()=>handleAssignment("Unassign")}
            disabled={selectedOperationIds.length < 1 || !hasAssignedUsers || showAssignDialog}
            size="small"
          >
            {t("UnAssignText")}
          </Button>
          <GridToolbarColumnsButton disabled={workAssignmentData.length === 0}>
          </GridToolbarColumnsButton>
          <Button
            disabled={workAssignmentInitialData.length === 0}
            onClick={handleDrawerOpen} 
            sx={{ border: 'none !important' }} 
            variant="outlined" 
            startIcon={
              filterCount > 0 ? (
                <Badge badgeContent={filterCount} color="primary">
                  <FilterListIcon />
                </Badge>
              ) : <FilterListIcon />
            }
            size="small"
          >
            {t("FiltersText")}
          </Button>
          <GridToolbarDensitySelector disabled={workAssignmentData.length === 0} />
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder={t("FilterForAnyFieldText")}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (apiRef.current) {
                apiRef.current.setQuickFilterValues([e.target.value]);
              }
            }}
            InputProps={{
              startAdornment: <FilterListIcon sx={{ mr: 1, color: '#000', fontSize: 'large' }} />
            }}
            sx={{
              width: '264px',
              height: '40px',
              '& .MuiOutlinedInput-root': {
                height: '40px',
                fontFamily: 'Roboto',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '143%',
                letterSpacing: '0.17px'
              }
            }}
          />
          </Stack>
         
        </Stack>

      </GridToolbarContainer>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '68vh' }}>
      <Paper style={{ height: gridHeight }}>
        {i18next.resolvedLanguage &&
          <DataGridPro
            apiRef={apiRef}
            key={("Grid" + i18next.language)}
            checkboxSelection
            sx={{
              '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
              '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
              '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' }
            }}
            localeText={{
              toolbarDensity: t('GenericDensityText'),
              toolbarColumns: t('GenericColumnsText'),
              columnsPanelTextFieldLabel: t('FindColumnText'),
              columnsPanelTextFieldPlaceholder: t('ColumnNameText'),
              columnsPanelHideAllButton: t('HideAllText'),
              columnsPanelShowAllButton: t('ShowAllText'),
              toolbarDensityCompact: t('GenericCompactText'),
              toolbarDensityStandard: t('GenericStandardText'),
              toolbarDensityComfortable: t('GenericComfortableText')
            }}
            components={{
              Toolbar: dataGridFilter,
              BaseCheckbox: (props) => {
                const title = props.disabled ? t('DisabledOperationTooltipTitleText') : "";
                return (
                  <Checkbox props={props} tooltipTitle={title} />)
              },
              NoRowsOverlay: () => (
                <Stack height="100%" alignItems="center" justifyContent="start" marginTop={3} key={("NoOps" + i18next.resolvedLanguage)}>
                  {t('NoOperationsFoundTextForPlantText')}
                </Stack>
              ),
            }}
            loading={gridDataLoading}
            hideFooter
            getRowHeight={() => 'auto'}
            getDetailPanelContent={({ row }) => <GridPanelContent row={row} />}
            getDetailPanelHeight={({ row }) => 'auto'}
            rows={workAssignmentData}
            columns={gridColumns}
            isRowSelectable={(params: GridRowParams) => !params.row.isOutsideSyncWindowInPast}
            onSelectionModelChange={(ids) => {
              const selectedIDs = new Set(ids);
              const selectedOperations = workAssignmentData.filter((row) =>
                selectedIDs.has(row.id)
              );
              setSelectedOperationIds(selectedOperations);
            }}
            columnVisibilityModel={columnVisibility}
            onColumnVisibilityModelChange={handleColumnVisibilityChange}
            componentsProps={{
              columnsPanel: {
                sx: {
                  '&.MuiDataGrid-columnsPanel': {
                    padding: '0px 0px 8px 8px'
                  },
                  '& .MuiDataGrid-panelContent': {
                    overflow: 'hidden',
                    '&::before': {
                      content: '"' + t('ColumnPreferencesAutoSavedText') + '"',
                      display: 'block',
                      width: '100%',
                      padding: '8px 8px 0px 8px',
                      color: 'text.secondary',
                      fontSize: '0.875rem'
                    }
                  }
                }
              }
            }}
          />
        }
      </Paper>
      <div>
        <RightFilterDrawer
          anchor="right"
          open={open}
          onClose={handleDrawerOpen}
          onOpen={handleDrawerOpen}
        />
      </div>
    </div>
  );
};

export default WorkAssignmentGrid;